import { type NativeSelectProps, isNativeSelectOptionsArray } from './types'
import { twMerge } from 'tailwind-merge'
import { useCallback, useRef } from 'react'
import { KeyboardKey } from '../../types'
import { validateNativeSelectOptionElement } from './utils/validateNativeSelectOptionElement'

export const NativeSelect: React.FC<
  React.PropsWithChildren<NativeSelectProps>
> = ({
  ariaLabel,
  children,
  className,
  defaultValue,
  id,
  onSelect,
  options,
}) => {
  const selectRef = useRef<HTMLSelectElement>(null)

  const openNativeSelect = useCallback(() => {
    if (selectRef.current) {
      selectRef.current.showPicker?.()
    }
  }, [])

  const handleLabelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        e.key === KeyboardKey.Space ||
        e.key === KeyboardKey.Enter ||
        e.key === KeyboardKey.ArrowDown
      ) {
        openNativeSelect()
      }
    },
    [openNativeSelect],
  )

  const labelId = `${id}-label`

  const defaultOption = (
    <option value="" selected disabled>
      {defaultValue}
    </option>
  )

  const renderOptions = (): React.ReactNode[] | undefined => {
    if (isNativeSelectOptionsArray(options)) {
      return [
        defaultOption,
        ...options.map((option, index) => (
          <option
            key={`${option.value}-${index}`}
            value={option.value}
            label={option.label}
            selected={false}
          >
            {option.label}
          </option>
        )),
      ]
    }

    return [
      defaultOption,
      ...options.map((option) => validateNativeSelectOptionElement(option)),
    ]
  }

  return (
    <div className={twMerge('relative w-full', className)}>
      <label
        id={labelId}
        aria-label={ariaLabel}
        htmlFor={id}
        tabIndex={0}
        role="button"
        aria-haspopup="listbox"
        aria-controls={id}
        className="absolute inset-0 pointer-events-none typography--body-md w-full text-left"
        onKeyDown={handleLabelKeyDown}
      >
        {children}
      </label>

      <select
        id={id}
        ref={selectRef}
        aria-labelledby={labelId}
        aria-describedby={labelId}
        className="text-transparent outline-none bg-transparent w-full h-full appearance-none"
        onChange={(e) => onSelect(e.target.value)}
      >
        {renderOptions()}
      </select>
    </div>
  )
}
